import { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api';

interface InsightThread {
  threadKey: string;
  channel: string;
  userId: string;
  messageCount: number;
  lastSeen: number;
  summary: string;
  topics: string[];
  sentiment: 'positive' | 'neutral' | 'negative';
  resolved: boolean;
  flagged: boolean;
  flagReason: string;
}

interface Digest {
  topTopics: { topic: string; count: number }[];
  sentiment: { positive: number; neutral: number; negative: number };
  resolution: { resolved: number; unresolved: number };
  flaggedCount: number;
}

interface LinkedIdentity {
  phone: string;
  appearances: { botId: string; channel: string }[];
}

interface InsightsData {
  exists: boolean;
  generatedAt?: number;
  threadCount?: number;
  processedCount?: number;
  threads?: InsightThread[];
  digest?: Digest;
  linkedIdentities?: LinkedIdentity[];
  cached?: boolean;
}

interface Props {
  bot: { id: string; businessName: string; [key: string]: any };
}

const CH: Record<string, string> = {
  wa: 'WhatsApp', web: 'Website', fb: 'Facebook', email: 'Email', voice: 'Voice',
};
const channelLabel = (c: string) => CH[c] || c;

const SENT_COLOR: Record<string, string> = {
  positive: '#10b981', neutral: '#6b7280', negative: '#ef4444',
};
const SENT_ICON: Record<string, string> = {
  positive: '😊', neutral: '😐', negative: '😞',
};

function timeAgo(ts: number): string {
  const m = Math.floor((Date.now() - ts) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function Badge({ label, color, bg }: { label: string; color: string; bg: string }) {
  return (
    <span style={{
      background: bg, color, fontSize: '11px', fontWeight: 600,
      padding: '2px 8px', borderRadius: '20px', whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

export default function InsightsPanel({ bot }: Props) {
  const [data, setData]         = useState<InsightsData | null>(null);
  const [loading, setLoading]   = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    apiFetch(`/api/insights/${bot.id}`)
      .then(r => r.json())
      .then((d: InsightsData) => setData(d))
      .catch(() => setData({ exists: false }))
      .finally(() => setLoading(false));
  }, [bot.id]);

  const generate = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await apiFetch(`/api/insights/${bot.id}/generate`, { method: 'POST', body: '{}' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Request failed (${res.status})`);
      }
      const result: InsightsData = await res.json();
      setData({ ...result, exists: true });
    } catch (e: any) {
      setError(e.message || 'Failed to generate insights');
    } finally {
      setGenerating(false);
    }
  };

  const card: React.CSSProperties = {
    background: 'var(--bg-card)', borderRadius: '12px',
    border: '1px solid var(--card-border)', padding: '20px', marginBottom: '16px',
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-faint)' }}>
      Loading insights…
    </div>
  );

  if (!data?.exists) return (
    <div style={{ maxWidth: '560px', margin: '0 auto', textAlign: 'center', padding: '60px 24px' }}>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>🧠</div>
      <h2 style={{ color: 'var(--text-primary)', fontSize: '20px', fontWeight: 700, margin: '0 0 12px' }}>
        CEO Insights
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '14px', lineHeight: 1.6, marginBottom: '24px' }}>
        Get AI-written summaries of every customer conversation, discover what your customers ask
        about most, flag unhappy customers, and spot when the same person contacts you across
        multiple channels.
      </p>
      <button
        onClick={generate}
        disabled={generating}
        style={{
          background: 'linear-gradient(135deg, #5B4FE8, #7c3aed)', color: 'white',
          border: 'none', borderRadius: '10px', padding: '13px 32px',
          fontSize: '15px', fontWeight: 600, cursor: generating ? 'not-allowed' : 'pointer',
          opacity: generating ? 0.7 : 1,
        }}
      >
        {generating ? '⏳ Analysing conversations…' : '🧠 Generate Insights'}
      </button>
      {generating && (
        <p style={{ color: 'var(--text-faint)', fontSize: '12px', marginTop: '12px' }}>
          This may take 20–60 seconds depending on conversation volume.
        </p>
      )}
      {error && <p style={{ color: '#ef4444', fontSize: '13px', marginTop: '12px' }}>{error}</p>}
    </div>
  );

  // Firebase can return null for empty arrays; guard all array fields
  const threads           = (data.threads          || []) as InsightThread[];
  const linkedIdentities  = (data.linkedIdentities || []) as LinkedIdentity[];
  const digest            = data.digest;
  const { generatedAt, threadCount, processedCount } = data;
  const flaggedThreads    = threads.filter(t => t.flagged);
  const unresolvedThreads = threads.filter(t => !t.resolved);

  return (
    <div style={{ maxWidth: '860px' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '8px' }}>
        <div>
          <h2 style={{ color: 'var(--text-primary)', fontSize: '18px', fontWeight: 700, margin: 0 }}>
            🧠 CEO Insights
          </h2>
          {generatedAt && (
            <div style={{ color: 'var(--text-faint)', fontSize: '12px', marginTop: '2px' }}>
              Generated {timeAgo(generatedAt)} · {processedCount} of {threadCount} threads analysed
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          {error && <span style={{ color: '#ef4444', fontSize: '12px' }}>{error}</span>}
          <button
            onClick={generate}
            disabled={generating}
            style={{
              background: 'var(--bg-element)', color: 'var(--text-muted)',
              border: '1px solid var(--border-subtle)', borderRadius: '8px',
              padding: '7px 14px', fontSize: '12px',
              cursor: generating ? 'not-allowed' : 'pointer', opacity: generating ? 0.6 : 1,
            }}
          >
            {generating ? '⏳ Regenerating…' : '↺ Regenerate'}
          </button>
        </div>
      </div>

      {/* ── Pattern Digest ── */}
      {digest && (
        <div style={card}>
          <h3 style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700, margin: '0 0 16px' }}>
            📌 Pattern Digest
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            {/* Sentiment */}
            <div style={{ background: 'var(--bg-element)', borderRadius: '8px', padding: '12px' }}>
              <div style={{ color: 'var(--text-faint)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>Sentiment</div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <span style={{ color: '#10b981', fontWeight: 600, fontSize: '13px' }}>😊 {digest.sentiment.positive}</span>
                <span style={{ color: '#6b7280', fontWeight: 600, fontSize: '13px' }}>😐 {digest.sentiment.neutral}</span>
                <span style={{ color: '#ef4444', fontWeight: 600, fontSize: '13px' }}>😞 {digest.sentiment.negative}</span>
              </div>
            </div>
            {/* Resolution */}
            <div style={{ background: 'var(--bg-element)', borderRadius: '8px', padding: '12px' }}>
              <div style={{ color: 'var(--text-faint)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>Resolution</div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ color: '#10b981', fontWeight: 600, fontSize: '13px' }}>✅ {digest.resolution.resolved}</span>
                <span style={{ color: '#f97316', fontWeight: 600, fontSize: '13px' }}>⏳ {digest.resolution.unresolved} open</span>
              </div>
            </div>
            {/* Flagged */}
            {digest.flaggedCount > 0 && (
              <div style={{ background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', padding: '12px' }}>
                <div style={{ color: '#ef4444', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Flagged</div>
                <div style={{ color: '#ef4444', fontWeight: 700, fontSize: '24px', lineHeight: 1 }}>{digest.flaggedCount}</div>
                <div style={{ color: '#ef4444', fontSize: '11px', marginTop: '2px' }}>need attention</div>
              </div>
            )}
          </div>
          {/* Top topics */}
          {(digest.topTopics?.length ?? 0) > 0 && (
            <>
              <div style={{ color: 'var(--text-faint)', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>
                Top topics
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {(digest.topTopics || []).map(({ topic, count }) => (
                  <span key={topic} style={{ background: 'rgba(91,79,232,0.1)', color: '#5B4FE8', fontSize: '12px', fontWeight: 500, padding: '4px 10px', borderRadius: '20px' }}>
                    {topic} <span style={{ opacity: 0.6 }}>×{count}</span>
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Flagged Conversations ── */}
      {flaggedThreads.length > 0 && (
        <div style={{ ...card, borderColor: 'rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.03)' }}>
          <h3 style={{ color: '#ef4444', fontSize: '14px', fontWeight: 700, margin: '0 0 14px' }}>
            🚩 Flagged — Need Attention ({flaggedThreads.length})
          </h3>
          {flaggedThreads.map(t => (
            <div key={t.threadKey} style={{ background: 'var(--bg-element)', borderRadius: '8px', padding: '12px', marginBottom: '8px', borderLeft: '3px solid #ef4444' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', flexWrap: 'wrap', gap: '4px' }}>
                <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>
                  {channelLabel(t.channel)} · {timeAgo(t.lastSeen)} · {t.messageCount} msgs
                </span>
                {t.flagReason && <span style={{ color: '#ef4444', fontSize: '11px', fontStyle: 'italic' }}>{t.flagReason}</span>}
              </div>
              <p style={{ color: 'var(--text-primary)', fontSize: '13px', margin: 0, lineHeight: 1.55 }}>{t.summary}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── Cross-channel links ── */}
      {linkedIdentities.length > 0 && (
        <div style={card}>
          <h3 style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700, margin: '0 0 12px' }}>
            🔗 Same Customer, Multiple Channels ({linkedIdentities.length})
          </h3>
          <p style={{ color: 'var(--text-faint)', fontSize: '12px', marginBottom: '12px', lineHeight: 1.5 }}>
            These phone numbers appeared on more than one channel or bot.
          </p>
          {linkedIdentities.slice(0, 8).map(link => (
            <div key={link.phone} style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', background: 'var(--bg-element)', borderRadius: '8px', padding: '10px 14px', marginBottom: '6px' }}>
              <span style={{ color: 'var(--text-muted)', fontSize: '12px', fontWeight: 500 }}>
                📞 …{link.phone.slice(-4)}
              </span>
              {(link.appearances || []).map((a, i) => (
                <Badge key={i} label={channelLabel(a.channel)} color="#5B4FE8" bg="rgba(91,79,232,0.1)" />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* ── All Thread Summaries ── */}
      <div style={card}>
        <h3 style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700, margin: '0 0 16px' }}>
          💬 Conversation Summaries ({threads.length})
        </h3>
        {threads.length === 0 ? (
          <p style={{ color: 'var(--text-faint)', fontSize: '13px' }}>No conversations to summarise yet.</p>
        ) : (
          threads.map((t, idx) => (
            <div
              key={t.threadKey}
              style={{
                paddingBottom: '14px', marginBottom: '14px',
                borderBottom: idx < threads.length - 1 ? '1px solid var(--border-subtle)' : 'none',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px', gap: '8px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: '11px', background: 'var(--bg-element)', padding: '2px 7px', borderRadius: '8px' }}>
                    {channelLabel(t.channel)}
                  </span>
                  <span style={{ color: SENT_COLOR[t.sentiment] || '#6b7280', fontSize: '14px' }}>
                    {SENT_ICON[t.sentiment] || '😐'}
                  </span>
                  {!t.resolved && <Badge label="Open" color="#f97316" bg="rgba(249,115,22,0.1)" />}
                  {t.flagged && <Badge label="Flagged" color="#ef4444" bg="rgba(239,68,68,0.1)" />}
                </div>
                <span style={{ color: 'var(--text-faint)', fontSize: '11px', flexShrink: 0 }}>
                  {timeAgo(t.lastSeen)} · {t.messageCount} msgs
                </span>
              </div>
              <p style={{ color: 'var(--text-primary)', fontSize: '13px', margin: '0 0 8px', lineHeight: 1.55 }}>{t.summary}</p>
              {(t.topics || []).length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {(t.topics || []).map(topic => (
                    <span key={topic} style={{ background: 'var(--bg-element)', color: 'var(--text-muted)', fontSize: '11px', padding: '2px 8px', borderRadius: '12px' }}>
                      {topic}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>

    </div>
  );
}
